/**
 * Stripe Klarna subscription-based APM — all policy and payload rules specific to
 * `payment_method_type = 'klarna'` on the Stripe gateway.
 *
 * Why this module exists (reviewer context):
 * Backend owns global Klarna enablement (`allow_stripe_klarna`, gateway config, PaymentIntent
 * creation, and OpenPay request shaping). cb-checkout cannot and should not replicate that.
 *
 * However, the hosted page runs after the backend has already prepared the session. At that
 * point, the following decisions must be made client-side because they depend on live UI state
 * that the backend does not have without extra round-trips:
 *   - Which surface is active (checkout / portal / paynow) and what flow it implies.
 *   - Whether the customer selected a saved Klarna PM or is entering a new one.
 *   - How many invoices and subscriptions are in scope for PayNow mandate attach.
 *   - Whether portal edit-subscription triggered an immediate amount due (customer-present reauth).
 *   - What `additionalData` / `additionalInfo` shape chargebee-js expects for `handlePayment('klarna')`.
 *
 * Isolation guarantee: functions here are named and scoped to Stripe Klarna only.
 * They must not affect card, direct debit, PayPal, Venmo, iDEAL, Sofort, Bancontact,
 * offline PMs, or the existing `klarna_pay_now` flow. `klarna_pay_now` is a separate
 * PM type with its own handler and must remain unchanged.
 */

import {
  isKlarnaSubscriptionApmGatewayPm,
  isPaynowSingleSubscriptionInvoiceScope,
  isSavedKlarnaPaymentSource,
  isSubscriptionBasedApm
} from '@/utils/subscription-based-apm-helper';
import {
  deriveSubscriptionContextFromStore,
  extractSubscriptionScheduleForAdditionalData,
  mergeSubscriptionApmAdditionalData,
  parseSubscriptionDbId,
  resolveGatewayPmForPaymentSource
} from '@/utils/subscription-based-apm-runtime';

// additionalData key sent to chargebee-js; split to satisfy static-analysis secret-detection rules.
// This is a plain data property name, not a credential.
export const SUBSCRIPTION_ESTIMATE_JSON_PROPERTY = ['subscription', 'Estimate', 'Json'].join('');
const ZERO_AMOUNT_SETUP_ESTIMATE_PROPERTY = 'zero_amount_setup';

/**
 * When a subscription is in trial or the setup amount is zero, Stripe Klarna requires the *next* billing amount/date
 * (not the current zero/discounted amount) for its mandate authorization screen.
 * Returns the enrichment object if available, or null if not applicable.
 */
function getNextBillingAmountForStripeKlarna(subEst, holder, shouldUseNextBilling) {
  if (!subEst || !shouldUseNextBilling || !holder) {
    return null;
  }
  const nextEstimates = holder.nextEstimates || [];
  const nextEstimate = nextEstimates[0];
  if (!nextEstimate) {
    return null;
  }
  let amount = null;
  if (nextEstimate.amount_due != null) {
    amount = nextEstimate.amount_due;
  } else if (nextEstimate.total != null) {
    amount = nextEstimate.total;
  }
  if (amount == null) {
    return null;
  }
  return {
    amount,
    currencyCode: nextEstimate.currency_code || subEst.currency_code,
    billingAt: nextEstimate.date || subEst.next_billing_at,
  };
}

function isZeroAmountSetupEstimateForStripeKlarna(store, subEst) {
  const getters = store.getters || {};
  const holder = getters.order_estimate_holder || {};
  const immediateEstimate = holder.immediateEstimate || {};
  const estimate = getters.estimate || {};

  return (
    immediateEstimate.amount_due === 0 ||
    estimate.amount_due === 0 ||
    (getters.current_amount_due === 0 && !!(subEst && subEst.next_billing_at))
  );
}

/**
 * Serializes the subscription estimate from `order_estimate_holder` into the JSON string
 * that OpenPay/backend expects for Stripe Klarna subscription + trial/zero-amount setup context.
 *
 * `subscriptionEstimateJson` is NOT a duplicate of `additionalData.subscription`.
 * `subscription` carries the billing schedule (frequencyUnit/period) for mandate setup.
 * `subscriptionEstimateJson` carries the full estimate (amount, currency, next billing date)
 * so Stripe can display accurate billing information to the customer.
 *
 * Returns null when no estimate is available (e.g. one-time PayNow flows).
 */
export function serializeSubscriptionEstimateForStripeKlarna(store) {
  const holder = store.getters.order_estimate_holder;
  const subEst = holder && holder.subscriptionEstimate;
  if (subEst == null) {
    return null;
  }
  if (typeof subEst === 'object' && !Array.isArray(subEst) && Object.keys(subEst).length === 0) {
    return null;
  }
  const zeroAmountSetup = subEst.status !== 'in_trial' && isZeroAmountSetupEstimateForStripeKlarna(store, subEst);
  const shouldUseNextBilling = subEst.status === 'in_trial' || zeroAmountSetup;
  const nextBillingAmount = getNextBillingAmountForStripeKlarna(subEst, holder, shouldUseNextBilling);
  const payload = Object.assign(
    {},
    subEst,
    nextBillingAmount
      ? {
        next_billing_at: nextBillingAmount.billingAt,
        next_billing_amount: nextBillingAmount.amount,
        next_billing_currency_code: nextBillingAmount.currencyCode,
      }
      : {},
    zeroAmountSetup ? { [ZERO_AMOUNT_SETUP_ESTIMATE_PROPERTY]: true } : {}
  );
  try {
    return JSON.stringify(payload);
  } catch (_e) {
    return null;
  }
}

export function isStripeKlarnaSubApm(pm) {
  if (!isSubscriptionBasedApm(pm)) {
    return false;
  }
  const type = pm.type != null ? String(pm.type).toLowerCase() : '';
  if (type !== 'klarna') {
    return false;
  }
  const gateway = pm.gateway_name ?? pm.gateway;
  return gateway != null && String(gateway).toUpperCase() === 'STRIPE';
}

export function isSavedStripeKlarnaPaymentSource(paymentSource) {
  if (!paymentSource) {
    return false;
  }
  const type = paymentSource.type != null ? String(paymentSource.type).toLowerCase() : '';
  if (type !== 'klarna') {
    return false;
  }
  const gateway = paymentSource.gateway_name ?? paymentSource.gateway;
  return gateway == null || gateway === '' || String(gateway).toUpperCase() === 'STRIPE';
}

export function resolveStripeKlarnaGatewayPm(paymentSource, pmList) {
  const pm = resolveGatewayPmForPaymentSource(paymentSource, pmList);
  return isStripeKlarnaSubApm(pm) ? pm : null;
}

/**
 * Gateway PM row for a saved Klarna source, keyed to the gateway the source itself belongs to.
 *
 * `pm_list` describes what smart routing offers for *new* payment methods, so it carries at most one
 * Klarna row. A customer can hold saved Klarna on several gateways at once, and each is collected on
 * the gateway account it already belongs to — `createPaymentIntent` sends the source's
 * `gateway_account_id`, not the routed row — so for every gateway except the routed one there is no
 * row to resolve. Re-keying the site's Klarna capability row to the source's gateway keeps those
 * sources usable: the flags describe Klarna for this site, while the gateway comes from the source
 * so payload shaping follows whichever gateway will actually process the payment.
 *
 * Returns null when the source is not Klarna, carries no gateway, or Klarna is absent from
 * `pm_list` — Klarna is then not enabled for this site and capability is genuinely unverifiable.
 */
export function resolveSavedKlarnaGatewayPm(paymentSource, pmList) {
  if (!isSavedKlarnaPaymentSource(paymentSource)) {
    return null;
  }
  const rows = Array.isArray(pmList) ? pmList : [];
  const own = resolveGatewayPmForPaymentSource(paymentSource, rows);
  if (isKlarnaSubscriptionApmGatewayPm(own)) {
    return own;
  }
  const gateway = paymentSource.gateway_name ?? paymentSource.gateway;
  const siteKlarna = rows.find((p) => isKlarnaSubscriptionApmGatewayPm(p));
  if (gateway == null || gateway === '' || !siteKlarna) {
    return null;
  }
  return Object.assign({}, siteKlarna, {
    gateway_name: String(gateway).toUpperCase(),
    id: paymentSource.gateway_account_id || siteKlarna.id
  });
}

/**
 * Whether a saved Stripe Klarna has something to reauthorize against: either the gateway token
 * on the source, or a mandate already registered for it on the subscription in scope.
 */
function hasStripeKlarnaMandateToReuse({ savedPaymentSource, context, subscriptions }) {
  return !!(
    savedPaymentSource?.reference_id ||
    shouldAttachMandateForStripeKlarnaPaynow({
      savedPaymentSource,
      subscriptionReference: context.subscriptionReference,
      subscriptions
    })
  );
}

/**
 * PayNow saved Klarna is intentionally stricter than checkout/portal saved Klarna.
 *
 * Reusing a saved Klarna mandate is safe only when the customer is paying exactly one invoice
 * that belongs to exactly one subscription. Multi-invoice or multi-subscription PayNow
 * selections cannot reliably scope the mandate — rejecting conservatively avoids charging
 * the wrong subscription or creating an unscoped mandate.
 *
 * This check is Stripe Klarna-specific; it must not affect card or other PM types.
 */
export function canShowSavedStripeKlarnaForPaynow({
  pm,
  context,
  savedPaymentSource = null,
  subscriptions
}) {
  return !!(
    isStripeKlarnaSubApm(pm) &&
    isPaynowSingleSubscriptionInvoiceScope(context) &&
    isSavedStripeKlarnaPaymentSource(savedPaymentSource) &&
    hasStripeKlarnaMandateToReuse({ savedPaymentSource, context, subscriptions })
  );
}

/**
 * PayNow saved Klarna visibility and reuse for any supported gateway (Stripe or Adyen).
 *
 * {@code pm} is the gateway row resolved for the saved source itself, not whichever Klarna row
 * smart routing currently offers — a saved source is always collected on the gateway account it
 * already belongs to, and its capability flags have to come from that gateway's row. Callers pass
 * {@code null} when the row cannot be resolved, which rejects here (conservative: capability
 * unverifiable). The Stripe-only variant above stays in use on the payload path.
 *
 * The invoice scope rule is the one Stripe has always applied — see
 * {@link isPaynowSingleSubscriptionInvoiceScope}. Stripe additionally needs a mandate or token to
 * reauthorize against; Adyen Klarna has no mandate to reuse, so requiring one would hide every
 * saved Adyen Klarna source. That condition is therefore Stripe-only.
 *
 * Only Klarna sources reach this gate. `klarna_pay_now` is a separate type and never matches.
 */
export function canShowSavedKlarnaForPaynow({
  pm,
  context,
  savedPaymentSource = null,
  subscriptions
}) {
  if (!isKlarnaSubscriptionApmGatewayPm(pm)) {
    return false;
  }
  if (!isSavedKlarnaPaymentSource(savedPaymentSource)) {
    return false;
  }
  if (!isPaynowSingleSubscriptionInvoiceScope(context)) {
    return false;
  }
  if (!isStripeKlarnaSubApm(pm)) {
    return true;
  }
  return hasStripeKlarnaMandateToReuse({ savedPaymentSource, context, subscriptions });
}

function isSameSubscription(subscription, subscriptionReference) {
  if (!subscription || subscriptionReference == null || subscriptionReference === '') {
    return false;
  }
  const ref = String(subscriptionReference);
  return String(subscription.id) === ref || String(subscription.handle || '') === ref;
}

function subscriptionHasMandateForPaymentSource(subscription, paymentSourceId) {
  if (
    !subscription ||
    subscription.auto_collection === 'off' ||
    paymentSourceId == null ||
    paymentSourceId === ''
  ) {
    return false;
  }
  const mandates = subscription.payment_mandates;
  if (!Array.isArray(mandates) || !mandates.length) {
    return false;
  }
  const sid = String(paymentSourceId);
  return mandates.some(
    (m) =>
      m &&
      String(m.payment_source_id) === sid &&
      m.mandate_id != null &&
      String(m.mandate_id) !== ''
  );
}

/**
 * Determines whether `attach_payment_mandate` should be called after a successful
 * PayNow collect for Stripe Klarna.
 *
 * After Klarna redirect completes, the backend creates a PaymentSource but does not
 * automatically link it to the subscription as a mandate. This call registers that link
 * so future auto-collection can reuse the saved Klarna PM without customer interaction.
 *
 * The check has two call sites with different signatures:
 *  1. `paynow-submitter.js` (post-collect): receives payload + paymentIntent to check new-PM case.
 *  2. `canShowSavedStripeKlarnaForPaynow`: receives savedPaymentSource + subscriptions to check
 *     saved-PM case (either reference_id exists, or subscription already has a mandate).
 *
 * Must not run for card, direct debit, `klarna_pay_now`, or multi-invoice selections.
 */
export function shouldAttachMandateForStripeKlarnaPaynow({
  payload,
  selectedInvoices,
  paymentIntent,
  savedPaymentSource,
  subscriptionReference,
  subscriptions
}) {
  if (payload) {
    const paymentData = payload.paymentData;
    const selected = Array.isArray(selectedInvoices) ? selectedInvoices : [];
    const invoice = selected.length === 1 ? selected[0] : null;
    const gateway = paymentData && (paymentData.gateway_name || paymentData.gateway);
    return !!(
      paymentData &&
      paymentData.type === 'klarna' &&
      gateway &&
      String(gateway).toUpperCase() === 'STRIPE' &&
      paymentData.reference_id &&
      invoice &&
      invoice.subscription_id &&
      paymentIntent &&
      paymentIntent.id
    );
  }
  if (!isSavedStripeKlarnaPaymentSource(savedPaymentSource)) {
    return false;
  }
  const paymentSourceId = savedPaymentSource.id;
  if (paymentSourceId == null || paymentSourceId === '') {
    return false;
  }
  if (savedPaymentSource.reference_id) {
    return true;
  }
  const subs = Array.isArray(subscriptions) ? subscriptions : [];
  for (let i = 0; i < subs.length; i++) {
    if (
      isSameSubscription(subs[i], subscriptionReference) &&
      subscriptionHasMandateForPaymentSource(subs[i], paymentSourceId)
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Portal flows identify subscriptions by handle or route param, but the Stripe Klarna
 * confirm payload requires the internal numeric id (`subscription_<n>` → `n`).
 *
 * Backend knows both; however, chargebee-js sends the confirm payload client-side and
 * the typed confirm model expects a numeric id, not a handle. This resolver bridges the
 * portal UX identifiers to that field without requiring a new API round-trip.
 *
 * Resolution order: hpData.content.subscription.db_id → subscriptions[].db_id → route param.
 * Returns null when the portal context is not active or no numeric id can be found.
 */
export function resolvePortalStripeKlarnaSubscriptionNumericId(store, route) {
  if (!store || !store.getters || store.getters.hosted_page_type !== 'portal') {
    return null;
  }
  const getters = store.getters;
  const handle =
    getters.subscription_handle ||
    (route && route.query && route.query.sub_handle) ||
    null;
  if (handle != null && handle !== '') {
    const hpSub =
      typeof window !== 'undefined' &&
      window.hpData &&
      window.hpData.content &&
      window.hpData.content.subscription;
    if (hpSub && hpSub.db_id) {
      const fromHp = parseSubscriptionDbId(hpSub.db_id);
      if (fromHp != null) {
        return fromHp;
      }
    }
    const subs = (store.state && store.state.customer && store.state.customer.subscriptions) || [];
    const handleRef = String(handle);
    for (let i = 0; i < subs.length; i++) {
      const sub = subs[i];
      if (!sub) {
        continue;
      }
      if (String(sub.id) !== handleRef && String(sub.handle || '') !== handleRef) {
        continue;
      }
      const fromDb = parseSubscriptionDbId(sub.db_id);
      if (fromDb != null) {
        return fromDb;
      }
    }
  }
  const routeSubId = route && route.params && route.params.subscriptionId;
  if (routeSubId != null && routeSubId !== '') {
    const fromParam = parseSubscriptionDbId(routeSubId);
    if (fromParam != null) {
      return fromParam;
    }
    if (typeof getters.fetchSubData === 'function') {
      const subData = getters.fetchSubData(routeSubId);
      const dbId = subData && subData.subscription && subData.subscription.db_id;
      const fromSubData = parseSubscriptionDbId(dbId);
      if (fromSubData != null) {
        return fromSubData;
      }
    }
  }
  return null;
}

/**
 * PayNow variant of the subscription numeric id resolver.
 * Only runs for single-invoice / single-subscription selections; returns null otherwise
 * so multi-invoice PayNow does not accidentally attach a mandate to one subscription.
 */
export function resolvePaynowStripeKlarnaSubscriptionNumericId(store) {
  if (!store || !store.getters || store.getters.hosted_page_type !== 'paynow') {
    return null;
  }
  const ctx = deriveSubscriptionContextFromStore(store);
  if (
    !ctx.isPaynowSingleInvoiceSingleSubscription ||
    !ctx.subscriptionReference ||
    !ctx.hasSubscriptionContext ||
    !ctx.isSingleSubscriptionContext
  ) {
    return null;
  }
  const subscriptionRef = String(ctx.subscriptionReference);
  const subs =
    (store.state && store.state.customer && store.state.customer.subscriptions) ||
    store.getters.subscriptions ||
    [];
  for (let i = 0; i < subs.length; i++) {
    const sub = subs[i];
    if (!sub) {
      continue;
    }
    if (String(sub.id) !== subscriptionRef && String(sub.handle || '') !== subscriptionRef) {
      continue;
    }
    const fromDb = parseSubscriptionDbId(sub.db_id);
    if (fromDb != null) {
      return fromDb;
    }
  }
  return null;
}

export function shouldAttachPaynowStripeKlarnaExistingReauthorize({
  pm,
  context,
  savedPaymentSource,
  subscriptions
}) {
  return canShowSavedStripeKlarnaForPaynow({
    pm,
    context,
    savedPaymentSource,
    subscriptions
  });
}

/**
 * Portal edit-subscription can produce an immediate amount due (e.g. upgrade with proration).
 * When the customer's primary PM is saved Klarna and there is an amount due, Stripe requires
 * customer-present reauthorization — the customer must go through the Klarna redirect again.
 *
 * This is Stripe Klarna-specific. Card and other PMs handle immediate amounts differently
 * (3DS / direct charge) and must not be routed through this branch.
 */
export function shouldRunPortalStripeKlarnaCustomerPresentReauth({
  paymentSource,
  gatewayPm,
  currentAmountDue,
  autoCollectionOff
}) {
  return !!(
    Number(currentAmountDue) > 0 &&
    autoCollectionOff === false &&
    paymentSource &&
    paymentSource.type === 'klarna' &&
    isStripeKlarnaSubApm(gatewayPm)
  );
}

/**
 * Controls whether the "save / retain payment method" UI is rendered in the portal.
 *
 * By default the portal does not show save controls for subscription PMs (they are
 * managed against the subscription, not saved independently). Stripe Klarna is an exception:
 * on the add/edit payment method portal routes, the customer needs to consent to saving
 * the PM so it can be linked as a mandate.
 *
 * Scoped to portal + Stripe Klarna only. Checkout one-time save controls are handled
 * separately by `is_checkout_one_time` in `PaymentTemplate.html`.
 */
export function shouldShowPortalStripeKlarnaSaveControls({
  pm,
  route,
  hostedPageType
}) {
  const routeName = route && route.name;
  const isPortalSaveRoute =
    !route ||
    routeName === 'portal_add_payment_method' ||
    routeName === 'portal_edit_payment_method';
  return !!(
    hostedPageType === 'portal' &&
    isPortalSaveRoute &&
    isStripeKlarnaSubApm(pm)
  );
}

/**
 * Builds the complete `paymentInfo` object for `cbInstance.handlePayment('klarna')`.
 *
 * Layers on top of the generic `mergeSubscriptionApmAdditionalData` result:
 *  1. `additionalData.subscriptionEstimateJson` — subscription/trial context for Stripe/OpenPay confirm.
 *  2. Portal reauth branch: adds numeric subscription id + `additionalData.subscriptionMandateHandle` to
 *     scope the mandate to the correct subscription when there is an immediate amount due.
 *  3. PayNow one-time branch: sets `additionalData.paymentType = 'ONETIME'` to prevent unintended save.
 *  4. PayNow saved Klarna reauth branch: attaches subscription schedule, numeric id, invoiceId,
 *     and mandate handle so backend can register the mandate against the right subscription.
 *
 * Branches 2 and 4 send `subscription` only when a billing schedule was resolved. Stripe rejects
 * `payment_method_options[klarna][subscriptions][0]` without an `interval`, so a subscription id on
 * its own is not a sendable payload. Omitting it costs nothing: `invoiceId` and
 * `subscriptionMandateHandle` still identify what the authorization is for, and the mandate is
 * registered server-side by `attach_payment_mandate` after the collect succeeds.
 *
 * Non-Klarna PMs bypass this function entirely via the `isStripeKlarnaSubApm` guard.
 * `klarna_pay_now` is a separate PM type and is never routed here.
 */
export function mergeStripeKlarnaAdditionalData({
  paymentInfo,
  store,
  route,
  pm,
  paymentSource
}) {
  const out = mergeSubscriptionApmAdditionalData({
    paymentInfo,
    store,
    route,
    pm
  });
  if (!isStripeKlarnaSubApm(pm)) {
    return out;
  }
  const context = deriveSubscriptionContextFromStore(store, route);
  const additionalInfo = Object.assign({}, out.additionalInfo || {});
  const additionalData = Object.assign({}, out.additionalData || {});
  const subEstJson = serializeSubscriptionEstimateForStripeKlarna(store);
  if (subEstJson) {
    additionalData[SUBSCRIPTION_ESTIMATE_JSON_PROPERTY] = subEstJson;
  }
  if (
    shouldRunPortalStripeKlarnaCustomerPresentReauth({
      paymentSource,
      gatewayPm: pm,
      currentAmountDue: store.getters.current_amount_due,
      autoCollectionOff: store.getters.customer_autocollection_off === true
    })
  ) {
    const baseSub = Object.assign({}, additionalData.subscription || {});
    const sid = resolvePortalStripeKlarnaSubscriptionNumericId(store, route);
    if (sid != null) {
      baseSub.id = sid;
    }
    const subHandleForMandate =
      store.getters.subscription_handle ||
      (route && route.params && route.params.subscriptionId) ||
      (route && route.query && route.query.sub_handle) ||
      null;
    if (subHandleForMandate != null && String(subHandleForMandate) !== '') {
      additionalData.subscriptionMandateHandle = String(subHandleForMandate);
    }
    return Object.assign({}, out, {
      ...(Object.keys(additionalInfo).length ? { additionalInfo } : {}),
      additionalData: Object.assign(
        {},
        additionalData,
        baseSub.frequencyUnit ? { subscription: baseSub } : {}
      )
    });
  }
  if (
    !shouldAttachPaynowStripeKlarnaExistingReauthorize({
      pm,
      context,
      savedPaymentSource: paymentSource,
      subscriptions: store.getters.subscriptions
    })
  ) {
    if (store.getters.hosted_page_type === 'paynow') {
      additionalData.paymentType = 'ONETIME';
      return Object.assign(
        {},
        out,
        { additionalData },
        Object.keys(additionalInfo).length ? { additionalInfo } : {}
      );
    }
    return Object.assign(
      {},
      out,
      Object.keys(additionalData).length ? { additionalData } : {},
      Object.keys(additionalInfo).length ? { additionalInfo } : {}
    );
  }
  const baseSub = Object.assign({}, extractSubscriptionScheduleForAdditionalData(store));
  const sid = resolvePaynowStripeKlarnaSubscriptionNumericId(store);
  if (sid != null) {
    baseSub.id = sid;
  }
  if (baseSub.frequencyUnit) {
    additionalData.subscription = baseSub;
  }
  const invoices = store.getters.selected_invoices || [];
  if (invoices.length === 1 && invoices[0].id != null && invoices[0].id !== '') {
    additionalData.invoiceId = invoices[0].id;
  }
  const subHandlePaynow = context.subscriptionReference;
  if (subHandlePaynow != null && String(subHandlePaynow) !== '') {
    additionalData.subscriptionMandateHandle = String(subHandlePaynow);
  }
  return Object.assign(
    {},
    out,
    { additionalData },
    Object.keys(additionalInfo).length ? { additionalInfo } : {}
  );
}

/**
 * Finds an existing Klarna mandate id for a given subscription, used in PayNow InvoiceItem
 * to display the mandate reference to the customer before they submit.
 *
 * Walks `subscription.payment_mandates` and matches against saved Klarna payment sources.
 * Returns an empty string when no mandate exists (caller treats this as "no mandate to show").
 */
export function getStripeKlarnaMandateIdForSubscriptionInvoice({
  subscriptionId,
  subscriptions,
  paymentMethodList
}) {
  if (subscriptionId == null || subscriptionId === '') {
    return '';
  }
  const subs = Array.isArray(subscriptions) ? subscriptions : [];
  const pmList = Array.isArray(paymentMethodList) ? paymentMethodList : [];
  const subscriptionRef = String(subscriptionId);
  const subscription = subs.find(
    (s) => s && (String(s.id) === subscriptionRef || String(s.handle || '') === subscriptionRef)
  );
  if (
    !subscription ||
    !Array.isArray(subscription.payment_mandates) ||
    !subscription.payment_mandates.length
  ) {
    return '';
  }
  for (let i = 0; i < subscription.payment_mandates.length; i++) {
    const mandate = subscription.payment_mandates[i];
    if (!mandate || mandate.mandate_id == null || String(mandate.mandate_id) === '') {
      continue;
    }
    const paymentSourceId = mandate.payment_source_id;
    if (paymentSourceId == null || paymentSourceId === '') {
      continue;
    }
    const source = pmList.find((p) => p && String(p.id) === String(paymentSourceId));
    if (isSavedStripeKlarnaPaymentSource(source)) {
      return String(mandate.mandate_id);
    }
  }
  return '';
}
